import { useEffect, useMemo, useState } from 'react';
import { extractErrorMessage } from '../api';
import { streamSearchV2 } from '../repositories/feedsV2Repository';
import { compareDraft, type DraftRow } from '../utils/draftPreviewV2';
import { buildComplete, type FeedV2State, INITIAL_STATE } from '../utils/feedsV2';

const TOP_K = 20;

function Row({ row }: { row: DraftRow }) {
  return <li className={`description-preview-row ${row.change}`}>
    <span className="description-preview-rank">{row.rank}</span>
    <div className="description-preview-story">
      <a href={row.url ?? undefined} target="_blank" rel="noreferrer">{row.title}</a>
      <span className="description-preview-meta">
        {row.change !== 'kept' && <span className={`description-preview-badge ${row.change}`}>{row.change === 'added' ? 'new' : 'dropped'}</span>}
        {row.canonical_domain}
      </span>
    </div>
  </li>;
}

/**
 * An admin's preview of a draft feed description on feeds v2: the draft
 * built exactly as the feed would be under it (an unsaved v2 search, streamed
 * here), its top stories beside the live feed's, and Promote, which makes the
 * draft the feed's description. Verdicts and grades are cached per
 * description, so nothing carries over or needs backfilling: the feed's next
 * build reads under the new description, and the draft's judging is already
 * in the cache it will read.
 */
export function DraftPreviewV2Panel({ draft, hours, live, busy, onPromote, onDismiss }: {
  draft: string;
  hours: number;
  /** The live feed's build. */
  live: FeedV2State;
  busy: boolean;
  onPromote: () => void;
  onDismiss: () => void;
}) {
  const [state, setState] = useState<FeedV2State>(INITIAL_STATE);
  useEffect(() => {
    setState(INITIAL_STATE);
    const controller = new AbortController();
    streamSearchV2({ query: draft, hours, signal: controller.signal, onUpdate: setState }).catch((err) => {
      if (!controller.signal.aborted) setState((prev) => ({ ...prev, done: true, error: extractErrorMessage(err) }));
    });
    return () => controller.abort();
  }, [draft, hours]);
  const comparison = useMemo(() => compareDraft(live.stories, state.stories, TOP_K), [live.stories, state.stories]);
  // A build the judge or grader failed, the draft's or the live feed's, is no comparison: nothing to promote on.
  const failed = state.done && !state.error && !buildComplete(state);
  const liveFailed = live.done && !buildComplete(live);
  const settled = buildComplete(live) && buildComplete(state);

  return <section className="description-preview-panel" aria-label="Description preview">
    <div className="beta-feed-prompt-header">
      <span className="beta-feed-prompt-title">Draft preview</span>
      <button type="button" className="beta-feed-prompt-action-btn beta-feed-prompt-close-btn" onClick={onDismiss}>Dismiss</button>
    </div>
    <p className="description-preview-draft">{draft}</p>
    {!state.done && <p className="beta-message" role="status">
      <span className="spinner" style={{ marginRight: 8 }} />
      Building the draft{state.feed ? ` (${state.waves.length} waves read)` : ''}…
    </p>}
    {state.error && <p className="beta-error" role="alert">Preview failed: {state.error}</p>}
    {failed && <p className="beta-error" role="alert">Preview incomplete: the draft&rsquo;s build got no usable judgments or grades. Try again.</p>}
    {liveFailed && <p className="beta-error" role="alert">No comparison: the live feed&rsquo;s build didn&rsquo;t finish. Retry the feed, then preview again.</p>}
    <p className="description-preview-counts">
      {settled ? `${comparison.added} new to the top ${TOP_K}, ${comparison.dropped} dropped from it` : 'Comparing as both builds land…'}
    </p>
    <div className="description-preview-columns">
      <div>
        <h3>Live feed</h3>
        <ol className="description-preview-list">{comparison.live.map(row => <Row key={row.key} row={row} />)}</ol>
      </div>
      <div>
        <h3>Draft</h3>
        <ol className="description-preview-list">{comparison.draft.map(row => <Row key={row.key} row={row} />)}</ol>
      </div>
    </div>
    <div className="description-preview-promote">
      <button type="button" className="beta-table-btn beta-primary-btn" disabled={busy || !settled} onClick={onPromote}>
        {busy ? 'Promoting…' : 'Promote draft'}
      </button>
    </div>
  </section>;
}
